<script setup lang="ts">
import { refDebounced, useEventListener, useLocalStorage } from '@vueuse/core'
import {
  useListMatchingSimulations,
  useLookupAdminCandidates,
  useSearchAdminCompanies,
  useSearchAdminJobs,
} from '@swipe4work/api-orval'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import { usePermissions } from '@/shared/composables/UsePermissions'
import { useCommandPalette } from '@/shared/composables/UseCommandPalette'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import { useComposedMenu } from '@/features/dashboard/composables/UseComposedMenu'
import { linkItemTypeIcon } from '@/features/dashboard/linkItemTypes'
import { useCompanyActions } from '@/features/companies/composables/useCompanyActions'
import CreateCompanyFormDialog from '@/features/companies/dialogs/CreateCompanyFormDialog.vue'
import CreateSalesDemoDialog from '@/features/sales/dialogs/CreateSalesDemoDialog.vue'
import CreateSalesDailyLogDialog from '@/features/sales/dialogs/CreateSalesDailyLogDialog.vue'

const MIN_TERM = 2
const MAX_PER_SECTION = 5
const MAX_RECENTS = 10

/*
 * ---------------------------------------------------------------------------
 * Mode — 'home' is the default omnisearch; scoped modes restrict to one type.
 * A pendingCompanyAction means scoped company search will execute that action
 * instead of navigating to the company detail page.
 * ---------------------------------------------------------------------------
 */

type PaletteMode = 'home' | 'search-companies' | 'search-jobs' | 'search-candidates'
type PendingCompanyAction = 'open-portal' | 'open-teamleader' | 'edit-company'

const MODE_META: Record<Exclude<PaletteMode, 'home'>, {
  label: string;
  icon: string;
  placeholder: string
}> = {
  'search-companies': {
    label: 'Zoek bedrijf',
    icon: 'fa-regular fa-building',
    placeholder: 'Zoek op bedrijfsnaam...',
  },
  'search-jobs': {
    label: 'Zoek vacature',
    icon: 'fa-regular fa-briefcase',
    placeholder: 'Zoek op vacaturetitel...',
  },
  'search-candidates': {
    label: 'Zoek kandidaat',
    icon: 'fa-regular fa-user',
    placeholder: 'Zoek op naam of e-mail...',
  },
}

const PENDING_ACTION_META: Record<PendingCompanyAction, {
  label: string;
  placeholder: string
}> = {
  'open-portal': {
    label: 'Inloggen in portaal',
    placeholder: 'Zoek bedrijf om in te loggen in het portaal...',
  },
  'open-teamleader': {
    label: 'Bedrijf openen in Teamleader',
    placeholder: 'Zoek bedrijf om te openen in Teamleader...',
  },
  'edit-company': {
    label: 'Bedrijf wijzigen',
    placeholder: 'Zoek bedrijf om te wijzigen...',
  },
}

const { isOpen, close, shortcutLabel } = useCommandPalette()
const { can } = usePermissions()
const router = useRouter()
const dialog = useDynamicDialog()
const companyActions = useCompanyActions()

const mode = ref<PaletteMode>('home')
const isScoped = computed(() => mode.value !== 'home')
const pendingCompanyAction = ref<PendingCompanyAction | null>(null)

const scopedMeta = computed(() => {
  if (!isScoped.value) return null
  if (mode.value === 'search-companies' && pendingCompanyAction.value) {
    return PENDING_ACTION_META[pendingCompanyAction.value]
  }

  return MODE_META[mode.value as Exclude<PaletteMode, 'home'>]
})

const inputPlaceholder = computed(() =>
  scopedMeta.value?.placeholder ?? 'Typ een zoekopdracht of actie...',
)

const term = ref('')
const debouncedTerm = refDebounced(term, 250)
const searchTerm = computed(() => debouncedTerm.value.trim())
const hasTerm = computed(() => searchTerm.value.length >= MIN_TERM)
const lowerTerm = computed(() => searchTerm.value.toLowerCase())

const inputEl = ref<HTMLInputElement | null>(null)

/*
 * ---------------------------------------------------------------------------
 * Types
 * ---------------------------------------------------------------------------
 */

type RecentEntry = {
  id: string
  label: string
  sublabel?: string
  icon: string
  kind: 'route' | 'url' | 'action'
  routeName?: string
  routeParams?: Record<string, string | number>
  url?: string
  urlTarget?: string
  actionKey?: string
}

type PaletteItem = {
  key: string
  label: string
  sublabel?: string
  icon: string
  run: () => void
  recent?: RecentEntry
  keepOpen?: boolean
  /** Extra (lowercase) zoektermen waarop dit item ook gevonden wordt. */
  keywords?: string[]
}

type ShowMore = {
  label: string
  route: {
    name: string;
    query?: Record<string, string>
  }
}

type PaletteSection = {
  key: string
  label: string
  items: PaletteItem[]
  pending: boolean
  showMore?: ShowMore
}

/*
 * ---------------------------------------------------------------------------
 * Permissions
 * ---------------------------------------------------------------------------
 */

const canCompanies = computed(() => can(AdminPermissionResource.Company, PermissionLevel.Read))
const canCompaniesWrite = computed(() => can(AdminPermissionResource.Company, PermissionLevel.Write))
const canJobs = computed(() => can(AdminPermissionResource.Job, PermissionLevel.Read))
const canCandidates = computed(() => can(AdminPermissionResource.Candidates, PermissionLevel.Read))
const canSimulations = computed(() => can(AdminPermissionResource.Simulations, PermissionLevel.Read))

/*
 * ---------------------------------------------------------------------------
 * Dialog-opening actions (create, etc.)
 * ---------------------------------------------------------------------------
 */

const dialogProps = {
  modal: true,
  closable: true,
  showHeader: false,
  dismissableMask: true,
}

const actionRunners: Record<string, () => void> = {
  'create-company': () => dialog.open(CreateCompanyFormDialog, { props: dialogProps }),
  'create-sales-demo': () => dialog.open(CreateSalesDemoDialog, { props: dialogProps }),
  'create-sales-log': () => dialog.open(CreateSalesDailyLogDialog, { props: dialogProps }),
}

const crudActionDefs = computed(() => [
  {
    key: 'create-company',
    label: 'Nieuw bedrijf',
    sublabel: 'Bedrijf of lead toevoegen',
    icon: 'fa-regular fa-building-circle-check',
    keywords: [
      'bedrijf aanmaken',
      'bedrijf toevoegen',
      'lead aanmaken',
      'nieuwe lead',
      'create company',
    ],
    allowed: canCompaniesWrite.value,
  },
  {
    key: 'create-sales-demo',
    label: 'Nieuwe demo',
    sublabel: 'Demo toevoegen aan je sales log',
    icon: 'fa-regular fa-calendar-plus',
    keywords: [
      'demo aanmaken',
      'demo toevoegen',
      'demo inplannen',
      'sales demo',
    ],
    allowed: can(AdminPermissionResource.SalesMetrics, PermissionLevel.Write),
  },
  {
    key: 'create-sales-log',
    label: 'Nieuwe sales log',
    sublabel: 'Dagelijkse sales activiteiten invoeren',
    icon: 'fa-regular fa-clipboard-list',
    keywords: [
      'sales log aanmaken',
      'dagelijkse log',
      'activiteiten invoeren',
      'sales activiteiten',
    ],
    allowed: can(AdminPermissionResource.SalesMetrics, PermissionLevel.Write),
  },
])

function toActionItem(def: {
  key: string;
  label: string;
  sublabel?: string;
  icon: string;
  keywords?: string[]
}): PaletteItem {
  return {
    key: `action-${def.key}`,
    label: def.label,
    sublabel: def.sublabel,
    icon: def.icon,
    keywords: def.keywords,
    run: () => actionRunners[def.key]?.(),
    recent: {
      id: `action-${def.key}`,
      label: def.label,
      sublabel: def.sublabel,
      icon: def.icon,
      kind: 'action',
      actionKey: def.key,
    },
  }
}

/*
 * Context actions: search-then-act flows for company operations. Each item
 * switches the palette into scoped company search with a pending action; the
 * selected company then receives that action instead of a detail navigation.
 */
const companyContextActionDefs = computed(() => {
  const items: {
    key: PendingCompanyAction;
    label: string;
    sublabel: string;
    icon: string;
    keywords: string[];
    allowed: boolean
  }[] = [
    {
      key: 'open-portal',
      label: 'Inloggen in portaal',
      sublabel: 'Inloggen als werkgever in het portaal',
      icon: 'fa-regular fa-globe',
      keywords: [
        'open in portaal',
        'bedrijf openen in portaal',
        'open bedrijf',
        'login portaal',
        'login als werkgever',
        'impersonate',
        'werkgever portaal',
        'portal',
      ],
      allowed: canCompanies.value,
    },
    {
      key: 'open-teamleader',
      label: 'Bedrijf openen in Teamleader',
      sublabel: 'Ga naar het bedrijf in Teamleader CRM',
      icon: 'fa-regular fa-arrow-up-right-from-square',
      keywords: [
        'open in teamleader',
        'teamleader',
        'crm',
        'open bedrijf',
      ],
      allowed: canCompanies.value,
    },
    {
      key: 'edit-company',
      label: 'Bedrijf wijzigen',
      sublabel: 'Open het wijzigingsformulier voor een bedrijf',
      icon: 'fa-regular fa-pen-to-square',
      keywords: [
        'bedrijf bewerken',
        'bedrijf aanpassen',
        'edit company',
        'update bedrijf',
      ],
      allowed: canCompaniesWrite.value,
    },
  ]

  return items.filter(i => i.allowed)
})

function toContextActionItem(def: {
  key: PendingCompanyAction;
  label: string;
  sublabel: string;
  icon: string;
  keywords: string[]
}): PaletteItem {
  return {
    key: `ctx-${def.key}`,
    label: def.label,
    sublabel: def.sublabel,
    icon: def.icon,
    keywords: def.keywords,
    keepOpen: true,
    run: () => enterCompanyContextMode(def.key),
  }
}

const actionItems = computed<PaletteItem[]>(() => {
  const allowedCrud = crudActionDefs.value.filter(a => a.allowed)
  const contextItems = companyContextActionDefs.value

  const allItems = [ ...allowedCrud.map(toActionItem), ...contextItems.map(toContextActionItem) ]

  return hasTerm.value
    ? allItems.filter(a =>
      a.label.toLowerCase().includes(lowerTerm.value)
      || a.keywords?.some(k => k.includes(lowerTerm.value)))
    : allItems
})

/*
 * ---------------------------------------------------------------------------
 * Links (LinkItems — server already filters visibility by role)
 * ---------------------------------------------------------------------------
 */

const { allItems: linkItems, navTarget, isPending: linksPending } = useComposedMenu()

const linkResults = computed<PaletteItem[]>(() => {
  if (!hasTerm.value) return []

  return linkItems.value
    .filter(item => item.name.toLowerCase().includes(lowerTerm.value))
    .slice(0, MAX_PER_SECTION)
    .map((item) => {
      const target = navTarget(item)
      const icon = item.icon || linkItemTypeIcon(item.type)

      const recent: RecentEntry = target.url
        ? {
          id: `link-${item.id}`,
          label: item.name,
          icon,
          kind: 'url',
          url: target.url,
          urlTarget: target.target ?? '_blank',
        }
        : {
          id: `link-${item.id}`,
          label: item.name,
          icon,
          kind: 'route',
          routeName: (target.route as any)?.name,
          routeParams: (target.route as any)?.params,
        }

      return {
        key: `link-${item.id}`,
        label: item.name,
        icon,
        run: () => runRecent(recent),
        recent,
      }
    })
})

/*
 * ---------------------------------------------------------------------------
 * Search queries (companies / jobs / candidates / simulations)
 * ---------------------------------------------------------------------------
 */

const companiesEnabled = computed(() => isOpen.value && hasTerm.value && canCompanies.value)
const { data: companiesData, isFetching: companiesFetching } = useSearchAdminCompanies(
  computed(() => ({
    search: searchTerm.value,
    Limit: MAX_PER_SECTION,
  })),
  { query: { enabled: companiesEnabled } },
)

const jobsEnabled = computed(() => isOpen.value && hasTerm.value && canJobs.value)
const { data: jobsData, isFetching: jobsFetching } = useSearchAdminJobs(
  computed(() => ({
    search: searchTerm.value,
    Limit: MAX_PER_SECTION,
  })),
  { query: { enabled: jobsEnabled } },
)

const candidatesEnabled = computed(() => isOpen.value && hasTerm.value && canCandidates.value)
const { data: candidatesData, isFetching: candidatesFetching } = useLookupAdminCandidates(
  computed(() => ({
    search: searchTerm.value,
    limit: MAX_PER_SECTION,
  })),
  { query: { enabled: candidatesEnabled } },
)

// No search param: fetch once and filter client-side.
const simulationsEnabled = computed(() => isOpen.value && canSimulations.value)
const { data: simulationsData, isPending: simulationsPending } = useListMatchingSimulations({
  query: {
    enabled: simulationsEnabled,
    staleTime: 60 * 1000,
  },
})

function routeItem(options: {
  key: string
  label: string
  sublabel?: string
  icon: string
  routeName: string
  routeParams: Record<string, string | number>
}): PaletteItem {
  const recent: RecentEntry = {
    id: options.key,
    label: options.label,
    sublabel: options.sublabel,
    icon: options.icon,
    kind: 'route',
    routeName: options.routeName,
    routeParams: options.routeParams,
  }

  return {
    key: options.key,
    label: options.label,
    sublabel: options.sublabel,
    icon: options.icon,
    run: () => runRecent(recent),
    recent,
  }
}

const companyResults = computed<PaletteItem[]>(() =>
  (companiesData.value?.companies ?? []).map((company) => {
    const sublabel = company.parentCompany
      ? `Onderdeel van ${company.parentCompany.name}`
      : (company.location?.city ?? undefined)

    /*
     * When a context action is pending, override the run function to execute
     * the queued action on the selected company instead of navigating.
     */
    if (pendingCompanyAction.value) {
      const action = pendingCompanyAction.value

      return {
        key: `company-${company.id}`,
        label: company.name,
        sublabel,
        icon: 'fa-regular fa-building',
        run: () => {
          if (action === 'open-portal') companyActions.loginToPortal(company.id)
          else if (action === 'open-teamleader') companyActions.openInTeamleader(company.teamleaderCompanyId)
          else if (action === 'edit-company') companyActions.openUpdateDialog(company.id)
          pendingCompanyAction.value = null
        },
        recent: {
          id: `company-${company.id}`,
          label: company.name,
          sublabel,
          icon: 'fa-regular fa-building',
          kind: 'route',
          routeName: 'companies.[id]',
          routeParams: { id: company.id },
        },
      }
    }

    return routeItem({
      key: `company-${company.id}`,
      label: company.name,
      sublabel,
      icon: 'fa-regular fa-building',
      routeName: 'companies.[id]',
      routeParams: { id: company.id },
    })
  }),
)

const jobResults = computed<PaletteItem[]>(() =>
  (jobsData.value?.jobs ?? []).map(job => routeItem({
    key: `job-${job.id}`,
    label: job.title,
    sublabel: job.companyName,
    icon: 'fa-regular fa-briefcase',
    routeName: 'vacatures.detail',
    routeParams: { id: job.id },
  })),
)

const candidateResults = computed<PaletteItem[]>(() =>
  (candidatesData.value?.candidates ?? []).map(candidate => routeItem({
    key: `candidate-${candidate.userId}`,
    label: candidate.displayName,
    sublabel: candidate.email,
    icon: 'fa-regular fa-user',
    routeName: '/matching/CandidateDetail',
    routeParams: { userId: candidate.userId },
  })),
)

const simulationResults = computed<PaletteItem[]>(() => {
  if (!hasTerm.value) return []

  return (simulationsData.value?.simulations ?? [])
    .filter(s => s.name.toLowerCase().includes(lowerTerm.value))
    .slice(0, MAX_PER_SECTION)
    .map(s => routeItem({
      key: `simulation-${s.id}`,
      label: s.name,
      sublabel: s.algorithmVersionName ?? undefined,
      icon: 'fa-regular fa-flask',
      routeName: '/matching/SimulationDetail',
      routeParams: { id: s.id },
    }))
})

/*
 * ---------------------------------------------------------------------------
 * Search mode actions — appear at the top so users can drill into one type.
 * ---------------------------------------------------------------------------
 */

function enterMode(newMode: Exclude<PaletteMode, 'home'>) {
  pendingCompanyAction.value = null
  mode.value = newMode
  nextTick(() => inputEl.value?.focus())
}

function enterCompanyContextMode(action: PendingCompanyAction) {
  pendingCompanyAction.value = action
  mode.value = 'search-companies'
  // De term waarmee de actie gevonden werd (bijv. "login") is geen bedrijfsnaam — begin met een leeg zoekveld.
  term.value = ''
  nextTick(() => inputEl.value?.focus())
}

const searchModeItems = computed<PaletteItem[]>(() => {
  const items: PaletteItem[] = []

  if (canCompanies.value) {
    items.push({
      key: 'mode-companies',
      label: 'Zoek bedrijf',
      sublabel: hasTerm.value ? `Zoek bedrijven voor "${searchTerm.value}"` : 'Gefocust zoeken in bedrijven',
      icon: 'fa-regular fa-building',
      keepOpen: true,
      run: () => enterMode('search-companies'),
    })
  }

  if (canJobs.value) {
    items.push({
      key: 'mode-jobs',
      label: 'Zoek vacature',
      sublabel: hasTerm.value ? `Zoek vacatures voor "${searchTerm.value}"` : 'Gefocust zoeken in vacatures',
      icon: 'fa-regular fa-briefcase',
      keepOpen: true,
      run: () => enterMode('search-jobs'),
    })
  }

  if (canCandidates.value) {
    items.push({
      key: 'mode-candidates',
      label: 'Zoek kandidaat',
      sublabel: hasTerm.value ? `Zoek kandidaten voor "${searchTerm.value}"` : 'Gefocust zoeken in kandidaten',
      icon: 'fa-regular fa-user',
      keepOpen: true,
      run: () => enterMode('search-candidates'),
    })
  }

  return items
})

/*
 * ---------------------------------------------------------------------------
 * Recents
 * ---------------------------------------------------------------------------
 */

const recents = useLocalStorage<RecentEntry[]>('s4w:command-palette-recents', [])

function recordRecent(entry: RecentEntry) {
  recents.value = [ entry, ...recents.value.filter(r => r.id !== entry.id) ].slice(0, MAX_RECENTS)
}

function runRecent(entry: RecentEntry) {
  if (entry.kind === 'action') {
    actionRunners[entry.actionKey ?? '']?.()

    return
  }

  if (entry.kind === 'url' && entry.url) {
    window.open(entry.url, entry.urlTarget ?? '_blank')

    return
  }

  if (entry.routeName) {
    router.push({
      name: entry.routeName,
      params: entry.routeParams,
    } as any)
  }
}

const recentItems = computed<PaletteItem[]>(() =>
  recents.value
    .filter(e => e.kind !== 'action' || crudActionDefs.value.some(a => a.key === e.actionKey && a.allowed))
    .map(e => ({
      key: `recent-${e.id}`,
      label: e.label,
      sublabel: e.sublabel,
      icon: e.icon,
      run: () => runRecent(e),
      recent: e,
    })),
)

const hasNoHistory = computed(() => recentItems.value.length === 0)

/*
 * ---------------------------------------------------------------------------
 * Sections
 * ---------------------------------------------------------------------------
 */

function makeCompanyShowMore(): ShowMore | undefined {
  if (!companiesData.value || companiesData.value.totalCount <= companyResults.value.length) return undefined

  return {
    label: `Toon alle ${companiesData.value.totalCount} bedrijven`,
    route: {
      name: 'companies.overview',
      query: hasTerm.value ? { search: searchTerm.value } : undefined,
    },
  }
}

function makeJobShowMore(): ShowMore | undefined {
  if (!jobsData.value || jobsData.value.totalCount <= jobResults.value.length) return undefined

  return {
    label: `Toon alle ${jobsData.value.totalCount} vacatures`,
    route: {
      name: 'vacatures.overview',
      query: hasTerm.value ? { search: searchTerm.value } : undefined,
    },
  }
}

function makeCandidateShowMore(): ShowMore | undefined {
  if (!candidatesData.value || candidatesData.value.candidates.length < MAX_PER_SECTION) return undefined

  return {
    label: 'Toon meer kandidaten',
    route: {
      name: '/matching/Candidates',
      query: hasTerm.value ? { search: searchTerm.value } : undefined,
    },
  }
}

const sections = computed<PaletteSection[]>(() => {
  if (mode.value !== 'home') {
    return getScopedSections()
  }

  // Home — idle state (no search term)
  if (!hasTerm.value) {
    const idle: PaletteSection[] = []

    if (recentItems.value.length > 0) {
      idle.push({
        key: 'recents',
        label: 'Recent bezocht',
        items: recentItems.value,
        pending: false,
      })
    }

    if (searchModeItems.value.length > 0) {
      idle.push({
        key: 'search-modes',
        label: 'Zoeken',
        items: searchModeItems.value,
        pending: false,
      })
    }

    if (actionItems.value.length > 0) {
      idle.push({
        key: 'actions',
        label: 'Acties',
        items: actionItems.value,
        pending: false,
      })
    }

    return idle
  }

  // Home — active search
  const result: PaletteSection[] = []

  if (actionItems.value.length > 0) {
    result.push({
      key: 'actions',
      label: 'Acties',
      items: actionItems.value,
      pending: false,
    })
  }

  result.push({
    key: 'links',
    label: 'Links',
    items: linkResults.value,
    pending: linksPending.value,
  })

  if (canCompanies.value) {
    result.push({
      key: 'companies',
      label: 'Bedrijven',
      items: companyResults.value,
      pending: companiesFetching.value,
      showMore: makeCompanyShowMore(),
    })
  }

  if (canJobs.value) {
    result.push({
      key: 'jobs',
      label: 'Vacatures',
      items: jobResults.value,
      pending: jobsFetching.value,
      showMore: makeJobShowMore(),
    })
  }

  if (canCandidates.value) {
    result.push({
      key: 'candidates',
      label: 'Kandidaten',
      items: candidateResults.value,
      pending: candidatesFetching.value,
      showMore: makeCandidateShowMore(),
    })
  }

  if (canSimulations.value) {
    result.push({
      key: 'simulations',
      label: 'Simulaties',
      items: simulationResults.value,
      pending: simulationsPending.value,
    })
  }

  return result.filter(s => s.items.length > 0 || s.pending)
})

function getScopedSections(): PaletteSection[] {
  switch (mode.value) {
    case 'search-companies':
      if (!canCompanies.value) return []

      return [
        {
          key: 'companies',
          label: 'Bedrijven',
          items: companyResults.value,
          pending: companiesFetching.value,
          showMore: makeCompanyShowMore(),
        },
      ].filter(s => s.items.length > 0 || s.pending)

    case 'search-jobs':
      if (!canJobs.value) return []

      return [
        {
          key: 'jobs',
          label: 'Vacatures',
          items: jobResults.value,
          pending: jobsFetching.value,
          showMore: makeJobShowMore(),
        },
      ].filter(s => s.items.length > 0 || s.pending)

    case 'search-candidates':
      if (!canCandidates.value) return []

      return [
        {
          key: 'candidates',
          label: 'Kandidaten',
          items: candidateResults.value,
          pending: candidatesFetching.value,
          showMore: makeCandidateShowMore(),
        },
      ].filter(s => s.items.length > 0 || s.pending)

    default:
      return []
  }
}

const flatItems = computed<PaletteItem[]>(() =>
  sections.value.flatMap((s) => {
    const items = [ ...s.items ]
    if (s.showMore && s.items.length > 0) {
      items.push({
        key: `show-more-${s.key}`,
        label: s.showMore.label,
        icon: 'fa-regular fa-list',
        run: () => navigateShowMore(s),
      })
    }

    return items
  }),
)
const isSearching = computed(() => hasTerm.value && sections.value.some(s => s.pending))
const showEmptyState = computed(() => hasTerm.value && flatItems.value.length === 0 && !isSearching.value)
const showScopedPrompt = computed(() => isScoped.value && !hasTerm.value && !isSearching.value)

const highlightedKey = ref<string | null>(null)

const highlightedIndex = computed(() =>
  flatItems.value.findIndex(i => i.key === highlightedKey.value),
)

watch(flatItems, (items) => {
  if (!items.some(i => i.key === highlightedKey.value)) {
    highlightedKey.value = null
  }
})

function focusItem(key: string) {
  nextTick(() => {
    const el = document.querySelector<HTMLElement>(`[data-palette-key="${CSS.escape(key)}"]`)

    el?.focus()
    el?.scrollIntoView({ block: 'nearest' })
  })
}

function focusInput() {
  highlightedKey.value = null
  nextTick(() => inputEl.value?.focus())
}

function moveHighlight(delta: number) {
  const items = flatItems.value

  if (items.length === 0) return

  const current = highlightedIndex.value

  // ArrowUp from the first item (or nothing highlighted) → return focus to input.
  if (delta < 0 && current <= 0) {
    focusInput()

    return
  }

  const next = current === -1
    ? 0
    : (current + delta + items.length) % items.length

  highlightedKey.value = items[next].key
  focusItem(items[next].key)
}

function onItemKeydown(event: KeyboardEvent) {
  if (event.key === 'Backspace') {
    event.preventDefault()
    term.value = term.value.slice(0, -1)
    focusInput()
  } else if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
    // Typing while on a button → append to search and return to input.
    event.preventDefault()
    term.value += event.key
    focusInput()
  }
}

function execute(item: PaletteItem) {
  if (item.recent) recordRecent(item.recent)
  if (!item.keepOpen) close()
  item.run()
}

function goBack() {
  mode.value = 'home'
  pendingCompanyAction.value = null
  nextTick(() => inputEl.value?.focus())
}

function navigateShowMore(section: PaletteSection) {
  if (!section.showMore) return
  close()
  router.push({
    name: section.showMore.route.name,
    query: section.showMore.route.query,
  } as any)
}

function onInputKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    moveHighlight(1)

    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    moveHighlight(-1)

    return
  }

  if (event.key === 'Backspace' && isScoped.value && term.value === '') {
    event.preventDefault()
    goBack()

    return
  }

  if (event.key === 'ArrowLeft' && isScoped.value) {
    const input = inputEl.value
    // Only go back when cursor is at the very start so normal text navigation still works.
    if (input && input.selectionStart === 0 && input.selectionEnd === 0) {
      event.preventDefault()
      goBack()

      return
    }
  }

  if (event.key === 'Enter') {
    // Enter from the input executes the first visible item as a quick shortcut.
    const item = flatItems.value[0]

    if (item) {
      event.preventDefault()
      execute(item)
    }
  }
}

/*
 * ---------------------------------------------------------------------------
 * Open/close lifecycle + global shortcuts
 * ---------------------------------------------------------------------------
 */

useEventListener(window, 'keydown', (event: KeyboardEvent) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    isOpen.value = !isOpen.value

    return
  }

  if (event.key === 'Escape' && isOpen.value) {
    event.preventDefault()
    if (isScoped.value) {
      goBack()
    } else {
      close()
    }
  }
})

function onShow() {
  term.value = ''
  mode.value = 'home'
  pendingCompanyAction.value = null
  highlightedKey.value = null
  nextTick(() => inputEl.value?.focus())
}
</script>

<template>
  <Dialog
    v-model:visible="isOpen"
    modal
    :show-header="false"
    :close-on-escape="false"
    dismissable-mask
    position="top"
    :style="{ width: '52rem', maxWidth: '95vw' }"
    content-class="!p-0"
    class="!mt-20"
    @show="onShow"
  >
    <div class="flex flex-col">
      <!-- Search input row -->
      <div class="flex items-center gap-x-3 border-b border-surface-100 px-5 py-4">
        <!-- Back button in scoped mode -->
        <button
          v-if="isScoped"
          type="button"
          class="flex shrink-0 items-center justify-center border-0 rounded-full bg-transparent p-1.5 text-surface-400 outline-none transition-colors hover:bg-surface-100 hover:text-surface-600"
          @click="goBack"
        >
          <i class="fa-regular fa-chevron-left" aria-hidden="true" />
        </button>

        <i v-else class="fa-regular fa-search shrink-0 text-lg text-surface-400" aria-hidden="true" />

        <input
          ref="inputEl"
          v-model="term"
          type="text"
          :placeholder="inputPlaceholder"
          class="min-w-0 flex-1 border-none bg-transparent text-base text-surface-900 outline-none placeholder:text-surface-400"
          @focus="highlightedKey = null"
          @keydown="onInputKeydown"
        >
        <kbd class="shrink-0 border border-surface-200 rounded-md bg-surface-50 px-2 py-1 text-xs text-surface-500">
          {{ shortcutLabel }}
        </kbd>
      </div>

      <!-- First-time welcome hint — only visible when nothing has been used yet -->
      <div
        v-if="hasNoHistory && !hasTerm && !isScoped"
        class="border-b border-surface-50 bg-surface-50/60 px-5 py-3"
      >
        <p class="text-sm text-surface-500 leading-relaxed">
          Zoek bedrijven, vacatures of kandidaten via de knoppen hieronder, of typ een naam om direct te zoeken.
          Je kunt ook acties uitvoeren zoals een nieuw bedrijf aanmaken of een bedrijf openen in het portaal.
        </p>
      </div>

      <!-- Results list -->
      <div class="max-h-[32rem] overflow-y-auto py-2">
        <template v-for="section in sections" :key="section.key">
          <p class="px-5 pb-1.5 pt-4 text-xs text-surface-400 font-semibold tracking-wide uppercase first:pt-2">
            {{ section.label }}
          </p>

          <template v-if="section.items.length > 0">
            <button
              v-for="item in section.items"
              :key="item.key"
              :data-palette-key="item.key"
              type="button"
              class="w-full flex cursor-pointer items-center gap-x-4 border-0 border-l-2 bg-transparent px-5 py-2.5 text-left outline-none transition-colors focus:outline-none"
              :class="item.key === highlightedKey
                ? 'border-l-primary-500 bg-primary-100 color-primary-800'
                : 'border-l-transparent hover:bg-surface-50'"
              @click="execute(item)"
              @mousemove="highlightedKey = item.key"
              @focus="highlightedKey = item.key"
              @keydown.down.prevent="moveHighlight(1)"
              @keydown.up.prevent="moveHighlight(-1)"
              @keydown.enter.prevent="execute(item)"
              @keydown="onItemKeydown"
            >
              <span class="h-10 w-10 flex shrink-0 items-center justify-center rounded-lg bg-surface-50 text-surface-600">
                <i :class="item.icon" aria-hidden="true" />
              </span>
              <span class="min-w-0 flex flex-col gap-y-0.5">
                <span class="truncate text-sm text-surface-900 font-medium">{{ item.label }}</span>
                <span v-if="item.sublabel" class="truncate text-xs text-surface-500">{{ item.sublabel }}</span>
              </span>
              <i
                v-if="item.keepOpen"
                class="fa-regular fa-chevron-right ml-auto shrink-0 text-xs text-surface-300"
                aria-hidden="true"
              />
            </button>

            <!-- Show more — navigates to the full list with the search term pre-filled -->
            <button
              v-if="section.showMore && section.items.length > 0"
              :data-palette-key="`show-more-${section.key}`"
              type="button"
              class="w-full flex cursor-pointer items-center gap-x-4 border-0 border-l-2 bg-transparent px-5 py-2.5 text-left outline-none transition-colors focus:outline-none"
              :class="`show-more-${section.key}` === highlightedKey
                ? 'border-l-primary-500 bg-primary-100 color-primary-800'
                : 'border-l-transparent hover:bg-surface-50'"
              @click="navigateShowMore(section)"
              @focus="highlightedKey = `show-more-${section.key}`"
              @mousemove="highlightedKey = `show-more-${section.key}`"
              @keydown.down.prevent="moveHighlight(1)"
              @keydown.up.prevent="moveHighlight(-1)"
              @keydown.enter.prevent="navigateShowMore(section)"
              @keydown="onItemKeydown"
            >
              <span class="h-10 w-10 flex shrink-0 items-center justify-center rounded-lg bg-surface-50 text-surface-400">
                <i class="fa-regular fa-list" aria-hidden="true" />
              </span>
              <span class="text-sm text-primary-500 font-medium">{{ section.showMore.label }}</span>
              <i class="fa-regular fa-arrow-right ml-auto shrink-0 text-xs text-surface-300" aria-hidden="true" />
            </button>
          </template>

          <!-- Skeleton rows while the section is loading -->
          <div v-else class="flex flex-col gap-y-1.5 px-5 py-1">
            <Skeleton v-for="n in 2" :key="n" height="2.5rem" />
          </div>
        </template>

        <!-- No results after a search -->
        <div v-if="showEmptyState" class="flex flex-col items-center gap-y-2 px-5 py-12 text-center">
          <i class="fa-regular fa-magnifying-glass text-3xl text-surface-300" aria-hidden="true" />
          <p class="text-sm text-surface-600 font-medium">
            Geen resultaten voor "{{ searchTerm }}"
          </p>
          <p class="text-xs text-surface-400">
            Probeer een andere zoekterm.
          </p>
        </div>

        <!-- Invite to type when in scoped mode before the user has entered a query -->
        <div v-else-if="showScopedPrompt" class="px-5 py-10 text-center">
          <i class="fa-regular fa-magnifying-glass mb-3 text-2xl text-surface-300" aria-hidden="true" />
          <p class="text-sm text-surface-500">
            Typ minimaal {{ MIN_TERM }} tekens om te zoeken.
          </p>
        </div>

        <div v-else-if="!hasTerm && flatItems.length === 0 && !isScoped" class="px-5 py-10 text-center text-sm text-surface-500">
          Typ minimaal {{ MIN_TERM }} tekens om te zoeken.
        </div>
      </div>
    </div>
  </Dialog>
</template>
